import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, OrgProvider, RequireAuth } from './lib/auth.js';
import { ToastProvider } from './components/ui.js';
import { AppLayout } from './layout/AppLayout.js';

import { LoginPage } from './features/auth/LoginPage.js';
import { WaitlistPage } from './features/auth/WaitlistPage.js';
import { AcceptInvitePage } from './features/auth/AcceptInvitePage.js';
import { CreateOrgPage } from './features/auth/CreateOrgPage.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';
import { AssessmentsPage } from './features/assessments/AssessmentsPage.js';
import { AssessmentDetailPage } from './features/assessments/AssessmentDetailPage.js';
import { ResponsePage } from './features/assessments/ResponsePage.js';
import { ReviewQueuePage } from './features/review/ReviewQueuePage.js';
import { CorpusPage } from './features/corpus/CorpusPage.js';
import { CorpusItemPage } from './features/corpus/CorpusItemPage.js';
import { EvidencePage } from './features/evidence/EvidencePage.js';
import { ExportsPage } from './features/exports/ExportsPage.js';
import { AuditPage } from './features/audit/AuditPage.js';
import { StandardsPage } from './features/standards/StandardsPage.js';
import { StandardDetailPage } from './features/standards/StandardDetailPage.js';
import { OrgSettingsPage } from './features/settings/OrgSettingsPage.js';
import { MembersPage } from './features/settings/MembersPage.js';
import { BillingPage } from './features/settings/BillingPage.js';
import { TokensPage } from './features/settings/TokensPage.js';
import { PlatformOverviewPage } from './features/admin/PlatformOverviewPage.js';
import { ManageOrgsPage } from './features/admin/ManageOrgsPage.js';
import { ManageStandardsPage } from './features/admin/ManageStandardsPage.js';
import { PlatformAuditPage } from './features/admin/PlatformAuditPage.js';
import { AssessorOverviewPage } from './features/assessor/AssessorOverviewPage.js';
import { AssessorReviewsPage } from './features/assessor/AssessorReviewsPage.js';
import { MyCoursesPage } from './features/training/MyCoursesPage.js';
import { CoursePage } from './features/training/CoursePage.js';
import { TrackTrainingPage } from './features/training/TrackTrainingPage.js';
import { MyPoliciesPage } from './features/policies/MyPoliciesPage.js';
import { PolicyReaderPage } from './features/policies/PolicyReaderPage.js';
import { PolicyTrackingPage } from './features/policies/PolicyTrackingPage.js';
import { DocumentRegisterPage } from './features/policies/DocumentRegisterPage.js';
import { BrandingPage } from './features/settings/BrandingPage.js';
import { UsagePage } from './features/settings/UsagePage.js';
import { ImportPage } from './features/import/ImportPage.js';
import { RecordsPage } from './features/records/RecordsPage.js';
import { AccessReviewsPage } from './features/records/AccessReviewsPage.js';
import { AccessReviewDetailPage } from './features/records/AccessReviewDetailPage.js';
import { DpiaCoveragePage } from './features/records/DpiaCoveragePage.js';
import { SoaPage } from './features/soa/SoaPage.js';
import { InternalAuditsPage } from './features/audits/InternalAuditsPage.js';
import { InternalAuditDetailPage } from './features/audits/InternalAuditDetailPage.js';
import { RecordsOverviewPage } from './features/records/RecordsOverviewPage.js';
import { MySoftwareAccessPage } from './features/records/MySoftwareAccessPage.js';
import { ManageHardwarePage } from './features/records/ManageHardwarePage.js';
import { ProfilePage } from './features/profile/ProfilePage.js';
import { NotFoundPage } from './features/misc/NotFoundPage.js';
import { ConsentBanner } from './components/ConsentBanner.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <OrgProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/waitlist" element={<WaitlistPage />} />
                <Route
                  path="/invitations/accept"
                  element={
                    <RequireAuth requireOrg={false}>
                      <AcceptInvitePage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/onboarding/create-org"
                  element={
                    <RequireAuth requireOrg={false}>
                      <CreateOrgPage />
                    </RequireAuth>
                  }
                />
                <Route
                  element={
                    <RequireAuth>
                      <AppLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="/assessments" element={<AssessmentsPage />} />
                  <Route path="/assessments/:assessmentId" element={<AssessmentDetailPage />} />
                  <Route
                    path="/assessments/:assessmentId/responses/:responseId"
                    element={<ResponsePage />}
                  />
                  <Route path="/assessments/:assessmentId/review" element={<ReviewQueuePage />} />
                  <Route path="/corpus" element={<CorpusPage />} />
                  <Route path="/corpus/:corpusItemId" element={<CorpusItemPage />} />
                  <Route path="/evidence" element={<EvidencePage />} />
                  <Route path="/exports" element={<ExportsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/audit" element={<AuditPage />} />
                  <Route path="/standards" element={<StandardsPage />} />
                  <Route path="/standards/versions/:versionId" element={<StandardDetailPage />} />
                  <Route path="/standards/versions/:versionId/soa" element={<SoaPage />} />
                  <Route path="/internal-audits" element={<InternalAuditsPage />} />
                  <Route path="/internal-audits/:auditId" element={<InternalAuditDetailPage />} />
                  <Route path="/training/my-courses" element={<MyCoursesPage />} />
                  <Route path="/training/courses/:courseId" element={<CoursePage />} />
                  <Route path="/training/track" element={<TrackTrainingPage />} />
                  <Route path="/policies" element={<MyPoliciesPage />} />
                  <Route path="/policies/tracking" element={<PolicyTrackingPage />} />
                  <Route path="/policies/register" element={<DocumentRegisterPage />} />
                  <Route path="/policies/:releaseId" element={<PolicyReaderPage />} />
                  <Route path="/settings" element={<OrgSettingsPage />} />
                  <Route path="/settings/members" element={<MembersPage />} />
                  <Route path="/settings/billing" element={<BillingPage />} />
                  <Route path="/settings/api" element={<TokensPage />} />
                  <Route path="/settings/branding" element={<BrandingPage />} />
                  <Route path="/settings/usage" element={<UsagePage />} />
                  <Route path="/import" element={<ImportPage />} />
                  <Route path="/assets" element={<RecordsOverviewPage group="inventory" />} />
                  <Route path="/registers" element={<RecordsOverviewPage group="registry" />} />
                  <Route path="/assets/software/access" element={<MySoftwareAccessPage />} />
                  <Route path="/assets/software/access-reviews" element={<AccessReviewsPage />} />
                  <Route
                    path="/assets/software/access-reviews/:reviewId"
                    element={<AccessReviewDetailPage />}
                  />
                  <Route path="/assets/hardware/manage" element={<ManageHardwarePage />} />
                  <Route path="/registers/dpias/coverage" element={<DpiaCoveragePage />} />
                  <Route path="/assets/:collection" element={<RecordsPage group="inventory" />} />
                  <Route path="/registers/:collection" element={<RecordsPage group="registry" />} />
                  <Route path="/admin" element={<PlatformOverviewPage />} />
                  <Route path="/admin/orgs" element={<ManageOrgsPage />} />
                  <Route path="/admin/standards" element={<ManageStandardsPage />} />
                  <Route path="/admin/audit" element={<PlatformAuditPage />} />
                  <Route path="/assessor" element={<AssessorOverviewPage />} />
                  <Route path="/assessor/reviews" element={<AssessorReviewsPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
              <ConsentBanner />
            </OrgProvider>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
